import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import {
  Users,
  Edit,
  Trash2,
  Search,
  UserPlus,
  Mail,
  Phone,
  Shield,
  CheckCircle,
  XCircle,
  AlertCircle,
  Key
} from 'lucide-react';

const GestionUsuarios = () => {
  const { API, user } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterActivo, setFilterActivo] = useState('activos'); // Por defecto mostrar solo activos
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creating, setCreating] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [editForm, setEditForm] = useState({
    nombre: '',
    apellido: '',
    email: '',
    celular: '',
    rol: '',
    activo: true,
    capacitado: true,  // Por defecto true para no afectar existentes
    foto_perfil: '',  // Campo opcional para URL de foto
    permisos_adicionales: { promotor_iba: false, acceso_marketing: false, genesis_access: false },  // Permisos adicionales
    // Campos que estaban fuera del modal
    mostrar_popup_registro: false,
    force_onesignal_prompt: false,
    es_graduado: false,
    compromiso_pago_habilitado: false,
    compromiso_pago_plazo_max: 15,
    compromiso_pago_motivo: '',
    pago_cuotas_3_habilitado: false,
    // Campos de iglesia
    nombre_iglesia: '',
    nombre_pastor: ''
  });
  const [createForm, setCreateForm] = useState({
    nombre: '',
    apellido: '',
    email: '',
    celular: '',
    password: '',
    rol: 'estudiante',
    generar_usuario_provisorio: false // Cambio a false por defecto para permitir contraseña personalizada
  });
  const [createdCredentials, setCreatedCredentials] = useState(null);
  
  // Estados para Compromiso de Pago
  const [isCompromisoDialogOpen, setIsCompromisoDialogOpen] = useState(false);
  const [compromisoConfig, setCompromisoConfig] = useState({
    habilitado: false,
    plazo_max: 15,
    motivo: ''
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        toast.error('Error al cargar usuarios');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditForm({
      nombre: user.nombre || '',
      apellido: user.apellido || '',
      email: user.email || '',
      celular: user.celular || '',
      fecha_nacimiento: user.fecha_nacimiento || '',
      rol: user.rol || '',
      activo: user.activo !== false,
      capacitado: user.capacitado !== false,  // Si es undefined/null → true
      foto_perfil: user.foto_perfil || '',  // Foto de perfil (opcional)
      permisos_adicionales: user.permisos_adicionales || { promotor_iba: false, acceso_marketing: false, genesis_access: false },  // Permisos adicionales
      // Campos que estaban fuera del modal
      mostrar_popup_registro: user.mostrar_popup_registro || false,
      force_onesignal_prompt: user.force_onesignal_prompt || false,
      es_graduado: user.es_graduado || false,
      compromiso_pago_habilitado: user.compromiso_pago_habilitado || false,
      compromiso_pago_plazo_max: user.compromiso_pago_plazo_max || 15,
      compromiso_pago_motivo: user.compromiso_pago_motivo || '',
      pago_cuotas_3_habilitado: user.pago_cuotas_3_habilitado || false,
      // Campos de iglesia
      nombre_iglesia: user.nombre_iglesia || '',
      nombre_pastor: user.nombre_pastor || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      // Separar permisos adicionales y campos especiales del resto de la actualización
      const { permisos_adicionales, mostrar_popup_registro, force_onesignal_prompt, es_graduado, compromiso_pago_habilitado, compromiso_pago_plazo_max, compromiso_pago_motivo, pago_cuotas_3_habilitado, nombre_iglesia, nombre_pastor, ...userUpdateData } = editForm;
      
      // Añadir campos de iglesia a la actualización básica
      userUpdateData.nombre_iglesia = nombre_iglesia;
      userUpdateData.nombre_pastor = nombre_pastor;
      
      // 1. Actualizar datos básicos del usuario
      const response = await fetch(`${API}/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(userUpdateData)
      });

      if (!response.ok) {
        const error = await response.json();
        toast.error(error.detail || 'Error al actualizar usuario');
        return;
      }

      // 2. Actualizar permisos adicionales si el rol es profesor o estudiante
      if (editForm.rol === 'profesor' || editForm.rol === 'estudiante') {
        const permisosResponse = await fetch(`${API}/admin/users/${selectedUser.id}/permisos`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            promotor_iba: permisos_adicionales?.promotor_iba || false,
            acceso_marketing: permisos_adicionales?.acceso_marketing || false,
            genesis_access: permisos_adicionales?.genesis_access || false
          })
        });

        if (!permisosResponse.ok) {
          const error = await permisosResponse.json();
          console.error('Error updating permisos:', error);
        }
      }

      // 3. Actualizar campos especiales en paralelo (sin bloquear)
      const updatePromises = [];

      // Popup registro (estudiantes, graduados, pastores)
      if (['estudiante', 'graduado', 'pastor'].includes(editForm.rol)) {
        if (mostrar_popup_registro !== selectedUser.mostrar_popup_registro) {
          updatePromises.push(
            fetch(`${API}/users/${selectedUser.id}/popup-registro`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
              body: JSON.stringify({ mostrar_popup_registro })
            })
          );
        }
      }

      // Force OneSignal prompt
      if (force_onesignal_prompt !== selectedUser.force_onesignal_prompt) {
        updatePromises.push(
          fetch(`${API}/users/${selectedUser.id}/force-onesignal-prompt`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify({ force_onesignal_prompt })
          })
        );
      }

      // Es graduado
      if (es_graduado !== selectedUser.es_graduado) {
        updatePromises.push(
          fetch(`${API}/users/${selectedUser.id}/es-graduado`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify({ es_graduado })
          })
        );
      }

      // Compromiso de pago (solo estudiantes)
      if (editForm.rol === 'estudiante') {
        if (compromiso_pago_habilitado !== selectedUser.compromiso_pago_habilitado || 
            compromiso_pago_plazo_max !== selectedUser.compromiso_pago_plazo_max) {
          updatePromises.push(
            fetch(`${API}/admin/users/${selectedUser.id}/compromiso-pago`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
              body: JSON.stringify({ 
                habilitado: compromiso_pago_habilitado, 
                plazo_max: compromiso_pago_plazo_max,
                motivo: compromiso_pago_motivo
              })
            })
          );
        }
        // Pago en 3 cuotas
        if (pago_cuotas_3_habilitado !== selectedUser.pago_cuotas_3_habilitado) {
          updatePromises.push(
            fetch(`${API}/admin/users/${selectedUser.id}/pago-cuotas-3`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
              body: JSON.stringify({ habilitado: pago_cuotas_3_habilitado })
            })
          );
        }
      }

      // Ejecutar todas las actualizaciones en paralelo
      await Promise.all(updatePromises);

      toast.success('Usuario actualizado exitosamente');
      setIsEditDialogOpen(false);
      
      // Actualizar el usuario localmente sin recargar toda la lista
      setUsers(prevUsers => prevUsers.map(u => {
        if (u.id === selectedUser.id) {
          return {
            ...u,
            ...userUpdateData,
            permisos_adicionales,
            mostrar_popup_registro,
            force_onesignal_prompt,
            es_graduado,
            compromiso_pago_habilitado,
            compromiso_pago_plazo_max,
            compromiso_pago_motivo,
            pago_cuotas_3_habilitado
          };
        }
        return u;
      }));
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Error de conexión');
    }
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await fetch(`${API}/users/${selectedUser.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Mostrar mensaje detallado con información del CASCADE DELETE
        if (data.total_deleted > 0) {
          toast.success(
            `✅ Usuario eliminado junto con ${data.total_deleted} registro${data.total_deleted > 1 ? 's' : ''} asociado${data.total_deleted > 1 ? 's' : ''}`,
            { duration: 5000 }
          );
          
          // Log detallado en consola para el admin
          console.log('🗑️ CASCADE DELETE completado:', {
            message: data.message,
            total_deleted: data.total_deleted,
            details: data.deleted_records
          });
        } else {
          toast.success('Usuario eliminado exitosamente (sin registros asociados)');
        }
        
        setIsDeleteDialogOpen(false);
        loadUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al eliminar usuario');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Error de conexión');
    }
  };

  // === COMPROMISO DE PAGO ===
  const handleCompromisoClick = async (user) => {
    setSelectedUser(user);
    // Cargar configuración actual del usuario
    try {
      const response = await fetch(`${API}/admin/users/${user.id}/compromiso-pago`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCompromisoConfig({
          habilitado: data.config?.habilitado || false,
          plazo_max: data.config?.plazo_max || 15,
          motivo: data.config?.motivo || ''
        });
      }
    } catch (error) {
      console.error('Error loading compromiso config:', error);
    }
    setIsCompromisoDialogOpen(true);
  };

  const handleCompromisoSubmit = async () => {
    if (!selectedUser) return;
    
    try {
      const response = await fetch(`${API}/admin/users/${selectedUser.id}/compromiso-pago`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(compromisoConfig)
      });

      if (response.ok) {
        toast.success(compromisoConfig.habilitado 
          ? `✅ Compromiso de pago habilitado para ${selectedUser.nombre}`
          : `Compromiso de pago deshabilitado para ${selectedUser.nombre}`
        );
        setIsCompromisoDialogOpen(false);
        loadUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al configurar compromiso');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    }
  };

  const handleResetPasswordClick = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setIsResetPasswordDialogOpen(true);
  };

  const handleResetPasswordConfirm = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setResettingPassword(true);
    try {
      const response = await fetch(`${API}/admin/reset-user-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          user_id: selectedUser.id,
          new_password: newPassword
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Contraseña actualizada exitosamente');
        setIsResetPasswordDialogOpen(false);
        setNewPassword('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al resetear contraseña');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Error de conexión');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleCreateClick = () => {
    // Additional security check
    if (user?.rol !== 'admin') {
      toast.error('Solo administradores pueden crear usuarios');
      return;
    }
    
    setCreateForm({
      nombre: '',
      apellido: '',
      email: '',
      celular: '',
      password: '',
      rol: 'estudiante',
      generar_usuario_provisorio: false
    });
    setCreatedCredentials(null);
    setIsCreateDialogOpen(true);
  };

  const handleCreateSubmit = async () => {
    try {
      setCreating(true);
      
      // Prepare payload - exclude password if auto-generating credentials
      const payload = { ...createForm };
      if (createForm.generar_usuario_provisorio) {
        delete payload.password; // Don't send password if auto-generating
      }
      
      const response = await fetch(`${API}/admin/create-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setCreatedCredentials(data.credenciales_temporales);
        toast.success('Usuario creado exitosamente');
        loadUsers();
        // Don't close dialog immediately, show credentials first
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al crear usuario');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Error de conexión');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateDialogClose = () => {
    setIsCreateDialogOpen(false);
    setCreatedCredentials(null);
    setCreateForm({
      nombre: '',
      apellido: '',
      email: '',
      celular: '',
      password: '',
      rol: 'estudiante',
      generar_usuario_provisorio: false
    });
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { label: 'Administrador', className: 'bg-red-500' },
      profesor: { label: 'Profesor', className: 'bg-blue-500' },
      estudiante: { label: 'Estudiante', className: 'bg-green-500' },
      pastor: { label: 'Pastor', className: 'bg-purple-500' },
      graduado: { label: 'Graduado', className: 'bg-amber-500' }
    };
    const config = roleConfig[role] || { label: role, className: 'bg-gray-500' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.apellido?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = filterRole === 'all' || user.rol === filterRole;
    
    // Filtro por estado activo/inactivo
    const matchesActivo = filterActivo === 'todos' || 
      (filterActivo === 'activos' && user.activo !== false) ||
      (filterActivo === 'inactivos' && user.activo === false);
    
    return matchesSearch && matchesRole && matchesActivo;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando usuarios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header compacto */}
        <div className="mb-4">
          <div className="bg-white rounded-xl shadow-lg border border-blue-100 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-4 py-4">
              <div className="flex flex-wrap justify-between items-center gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <h1 className="text-xl font-bold text-white flex items-center gap-2">
                    Gestión de Usuarios
                    {!loading && (
                      <span className="text-sm font-normal text-blue-100">
                        ({users.length})
                      </span>
                    )}
                  </h1>
                </div>
                
                {!loading && users.length > 0 && (
                  <div className="hidden sm:flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 text-sm">
                    <span className="text-white" title="Administradores">
                      <i className="fas fa-shield-alt text-red-300 mr-1"></i>
                      {users.filter(u => u.rol === 'admin').length}
                    </span>
                    <span className="text-white" title="Profesores">
                      <i className="fas fa-chalkboard-teacher text-blue-300 mr-1"></i>
                      {users.filter(u => u.rol === 'profesor').length}
                    </span>
                    <span className="text-white" title="Estudiantes">
                      <i className="fas fa-user-graduate text-green-300 mr-1"></i>
                      {users.filter(u => u.rol === 'estudiante').length}
                    </span>
                    <span className="text-white" title="Pastores">
                      <i className="fas fa-church text-purple-300 mr-1"></i>
                      {users.filter(u => u.rol === 'pastor').length}
                    </span>
                    <span className="text-white" title="Graduados">
                      <i className="fas fa-graduation-cap text-amber-300 mr-1"></i>
                      {users.filter(u => u.rol === 'graduado').length}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Filtros compactos + Leyenda */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 mb-4 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por nombre o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-10 border-gray-200"
              />
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-[160px] h-10 border-gray-200">
                <SelectValue placeholder="Rol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los roles</SelectItem>
                <SelectItem value="admin">Administradores</SelectItem>
                <SelectItem value="profesor">Profesores</SelectItem>
                <SelectItem value="estudiante">Estudiantes</SelectItem>
                <SelectItem value="pastor">Pastores</SelectItem>
                <SelectItem value="graduado">Graduados</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterActivo} onValueChange={setFilterActivo}>
              <SelectTrigger className="w-[130px] h-10 border-gray-200">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="activos">Activos</SelectItem>
                <SelectItem value="inactivos">Inactivos</SelectItem>
              </SelectContent>
            </Select>
            {user?.rol === 'admin' && (
              <Button 
                onClick={handleCreateClick}
                className="h-10 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Crear
              </Button>
            )}
          </div>
          
          {/* Leyenda de íconos */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
            <span className="font-medium text-gray-600">Íconos:</span>
            <span>📋 Form. pendiente</span>
            <span>📝 Popup registro</span>
            <span>🔔 Forzar notif.</span>
            <span>⭐ Graduado VIP</span>
            <span>💳 Compromiso pago</span>
            <span>✅ Capacitado</span>
            <span>⚠️ Sin capacitar</span>
            <span>🔒 Bloqueado</span>
          </div>
        </div>

        {/* Users List */}
        <div className="max-h-[calc(100vh-320px)] overflow-y-auto border border-gray-200 rounded-lg bg-gray-50 p-4">
          <div className="grid grid-cols-1 gap-3">
            {filteredUsers.map((userItem) => (
              <Card key={userItem.id} className="hover:shadow-md transition-shadow bg-white">
              <CardContent className="p-3">
                {/* Mobile: stack vertically, Desktop: single row */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
                  {/* Avatar + Name + Email */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {userItem.foto_perfil ? (
                      <img 
                        src={userItem.foto_perfil} 
                        alt={`${userItem.nombre} ${userItem.apellido}`}
                        className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 items-center justify-center text-white font-bold text-sm flex-shrink-0 ${userItem.foto_perfil ? 'hidden' : 'flex'}`}
                    >
                      {userItem.nombre?.charAt(0).toUpperCase()}{userItem.apellido?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900 text-sm truncate">
                          {userItem.nombre} {userItem.apellido}
                        </h3>
                        {getRoleBadge(userItem.rol)}
                        {!userItem.activo && (
                          <Badge variant="destructive" className="text-xs">
                            <XCircle className="h-3 w-3 mr-1" />
                            Inactivo
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        <Mail className="h-3 w-3 inline mr-1" />
                        {userItem.email}
                      </p>
                    </div>
                  </div>
                  
                  {/* Status icons + Buttons: row on mobile, part of main row on desktop */}
                  <div className="flex items-center justify-between sm:justify-end gap-2 pl-13 sm:pl-0">
                    {/* Status icons */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {userItem.pendiente_formulario_registro && (
                        <span title="Formulario de registro pendiente" className="text-indigo-500 text-sm">📋</span>
                      )}
                      {userItem.mostrar_popup_registro && (
                        <span title="Popup registro activo" className="text-blue-500 text-sm">📝</span>
                      )}
                      {userItem.force_onesignal_prompt && (
                        <span title="Forzar notificaciones" className="text-orange-500 text-sm">🔔</span>
                      )}
                      {userItem.es_graduado && (
                        <span title="Graduado IBA (VIP)" className="text-purple-500 text-sm">⭐</span>
                      )}
                      {userItem.compromiso_pago_habilitado && (
                        <span title="Compromiso de pago" className="text-amber-500 text-sm">💳</span>
                      )}
                      {userItem.rol === 'profesor' && userItem.capacitado && (
                        <span title="Profesor capacitado" className="text-green-500 text-sm">✅</span>
                      )}
                      {userItem.rol === 'profesor' && userItem.capacitado === false && (
                        <span title="Pendiente capacitación" className="text-red-500 text-sm">⚠️</span>
                      )}
                      {userItem.usuario_bloqueado_pago && (
                        <span title="Bloqueado por pago" className="text-red-600 text-sm">🔒</span>
                      )}
                    </div>
                    
                    {/* Action buttons */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditClick(userItem)}
                        className="text-xs px-2 py-1 h-7"
                      >
                        <Edit className="h-3.5 w-3.5 sm:mr-1" />
                        <span className="hidden sm:inline">Editar</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResetPasswordClick(userItem)}
                        className="text-xs px-2 py-1 h-7 border-blue-300 text-blue-600 hover:bg-blue-50"
                        title="Cambiar contraseña"
                      >
                        <Key className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteClick(userItem)}
                        className="text-xs px-2 py-1 h-7"
                        title="Eliminar usuario"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Results Summary */}
      <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
        <span>
          Mostrando {filteredUsers.length} de {users.length} usuarios
          {searchTerm && ` (filtrado por: "${searchTerm}")`}
          {filterRole !== 'all' && ` (rol: ${filterRole})`}
        </span>
        {filteredUsers.length > 5 && (
          <span className="text-xs text-gray-500">
            💡 Use scroll para ver más usuarios
          </span>
        )}
      </div>

      {filteredUsers.length === 0 && !loading && (
        <div className="max-h-[calc(100vh-400px)] overflow-y-auto border border-gray-200 rounded-lg bg-gray-50 p-4">
          <Card className="bg-white">
            <CardContent className="p-12 text-center">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-700 mb-2">No se encontraron usuarios</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || filterRole !== 'all' 
                  ? 'Prueba ajustando los filtros de búsqueda'
                  : 'No hay usuarios registrados en el sistema'
                }
              </p>
              {user?.rol === 'admin' && !searchTerm && filterRole === 'all' && (
                <Button 
                  onClick={handleCreateClick}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Crear Primer Usuario
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center gap-3 text-2xl">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-md">
                <Edit className="h-6 w-6 text-white" />
              </div>
              <span className="bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent font-bold">
                Editar Usuario
              </span>
            </DialogTitle>
            <DialogDescription className="text-gray-600 mt-2">
              Modifica la información del usuario. Los campos marcados con * son obligatorios.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto px-1 py-4">
            {/* Información Personal */}
            <div className="mb-6">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-blue-100">
                <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full"></div>
                Información Personal
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre" className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <span className="text-red-500">*</span> Nombre
                  </Label>
                  <div className="relative">
                    <Input
                      id="nombre"
                      value={editForm.nombre}
                      onChange={(e) => setEditForm({...editForm, nombre: e.target.value})}
                      className="pl-10 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                      placeholder="Ingrese el nombre"
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <i className="fas fa-user text-sm"></i>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="apellido" className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <span className="text-red-500">*</span> Apellido
                  </Label>
                  <div className="relative">
                    <Input
                      id="apellido"
                      value={editForm.apellido}
                      onChange={(e) => setEditForm({...editForm, apellido: e.target.value})}
                      className="pl-10 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                      placeholder="Ingrese el apellido"
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <i className="fas fa-user text-sm"></i>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="fecha-nacimiento" className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <i className="fas fa-calendar text-blue-500 text-xs mr-1"></i> Fecha de Nacimiento
                  </Label>
                  <div className="relative">
                    <Input
                      id="fecha-nacimiento"
                      type="date"
                      value={editForm.fecha_nacimiento || ''}
                      onChange={(e) => setEditForm({...editForm, fecha_nacimiento: e.target.value})}
                      className="pl-10 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <i className="fas fa-birthday-cake text-sm"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Información de Contacto */}
            <div className="mb-6">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-green-100">
                <div className="w-1 h-6 bg-gradient-to-b from-green-500 to-green-600 rounded-full"></div>
                Información de Contacto
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <span className="text-red-500">*</span> Email
                  </Label>
                  <div className="relative">
                    <Input
                      id="email"
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                      className="pl-10 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                      placeholder="correo@ejemplo.com"
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <Mail className="h-4 w-4" />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="celular" className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <i className="fas fa-mobile text-green-500 text-xs mr-1"></i> Celular
                  </Label>
                  <div className="relative">
                    <Input
                      id="celular"
                      value={editForm.celular}
                      onChange={(e) => setEditForm({...editForm, celular: e.target.value})}
                      className="pl-10 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                      placeholder="+598 99 123 456"
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <Phone className="h-4 w-4" />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="foto_perfil" className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <i className="fas fa-image text-purple-500 text-xs mr-1"></i> URL Foto de Perfil (Opcional)
                  </Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input
                        id="foto_perfil"
                        placeholder="https://ejemplo.com/foto.jpg"
                        value={editForm.foto_perfil || ''}
                        onChange={(e) => setEditForm({...editForm, foto_perfil: e.target.value})}
                        className="pl-10 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                      />
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                        <i className="fas fa-camera text-sm"></i>
                      </div>
                    </div>
                    {editForm.foto_perfil && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setEditForm({...editForm, foto_perfil: ''})}
                        className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <i className="fas fa-times"></i>
                        Limpiar
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <i className="fas fa-info-circle text-blue-400"></i>
                    Ingrese la URL completa de la imagen del perfil
                  </p>
                  {editForm.foto_perfil && editForm.foto_perfil.startsWith('data:') && (
                    <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800 flex items-start gap-2">
                      <i className="fas fa-exclamation-triangle mt-0.5"></i>
                      <span>
                        <strong>Nota:</strong> Actualmente tiene una imagen codificada. Use el botón &quot;Limpiar&quot; para borrarla e ingresar una URL normal.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Rol y Permisos */}
            <div className="mb-6">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-purple-100">
                <div className="w-1 h-6 bg-gradient-to-b from-purple-500 to-purple-600 rounded-full"></div>
                Rol y Permisos
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rol" className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <span className="text-red-500">*</span> Rol del Usuario
                  </Label>
                  <div className="relative">
                    <Select value={editForm.rol} onValueChange={(value) => setEditForm({...editForm, rol: value})}>
                      <SelectTrigger className="pl-10 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                        <SelectValue placeholder="Seleccionar rol" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-red-500" />
                            <span className="font-medium">Administrador</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="profesor">
                          <div className="flex items-center gap-2">
                            <i className="fas fa-chalkboard-teacher text-blue-500"></i>
                            <span className="font-medium">Profesor</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="estudiante">
                          <div className="flex items-center gap-2">
                            <i className="fas fa-user-graduate text-green-500"></i>
                            <span className="font-medium">Estudiante</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="pastor">
                          <div className="flex items-center gap-2">
                            <i className="fas fa-church text-purple-500"></i>
                            <span className="font-medium">Pastor</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="graduado">
                          <div className="flex items-center gap-2">
                            <i className="fas fa-graduation-cap text-amber-500"></i>
                            <span className="font-medium">Graduado</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                      <Shield className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Información de Iglesia */}
            <div className="mb-4">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-cyan-100">
                <div className="w-1 h-6 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full"></div>
                <i className="fas fa-church text-cyan-600"></i>
                Información de Iglesia
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre_iglesia" className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <i className="fas fa-church text-cyan-500"></i> Nombre de la Iglesia
                  </Label>
                  <Input
                    id="nombre_iglesia"
                    value={editForm.nombre_iglesia}
                    onChange={(e) => setEditForm({...editForm, nombre_iglesia: e.target.value})}
                    placeholder="Ej: Iglesia Apostólica..."
                    className="border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nombre_pastor" className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <i className="fas fa-user-tie text-cyan-500"></i> Nombre del Pastor
                  </Label>
                  <Input
                    id="nombre_pastor"
                    value={editForm.nombre_pastor}
                    onChange={(e) => setEditForm({...editForm, nombre_pastor: e.target.value})}
                    placeholder="Ej: Pastor Juan..."
                    className="border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
                  />
                </div>
              </div>
            </div>

            {/* Estado del Usuario */}
            <div className="mb-4">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-amber-100">
                <div className="w-1 h-6 bg-gradient-to-b from-amber-500 to-amber-600 rounded-full"></div>
                Estado del Usuario
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-200 hover:border-green-300 transition-all cursor-pointer">
                  <input
                    type="checkbox"
                    id="activo"
                    checked={editForm.activo}
                    onChange={(e) => setEditForm({...editForm, activo: e.target.checked})}
                    className="mt-1 h-5 w-5 text-green-600 focus:ring-2 focus:ring-green-500 rounded cursor-pointer"
                  />
                  <Label htmlFor="activo" className="cursor-pointer flex-1">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="font-semibold text-green-900">Usuario Activo</span>
                    </div>
                    <p className="text-sm text-green-700 mt-1">
                      El usuario puede iniciar sesión y acceder al sistema
                    </p>
                  </Label>
                </div>
                
                {/* Checkbox Capacitado solo para profesores */}
                {editForm.rol === 'profesor' && (
                  <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-blue-50 to-sky-50 rounded-lg border-2 border-blue-200 hover:border-blue-300 transition-all cursor-pointer">
                    <input
                      type="checkbox"
                      id="capacitado"
                      checked={editForm.capacitado}
                      onChange={(e) => setEditForm({...editForm, capacitado: e.target.checked})}
                      className="mt-1 h-5 w-5 text-blue-600 focus:ring-2 focus:ring-blue-500 rounded cursor-pointer"
                    />
                    <Label htmlFor="capacitado" className="cursor-pointer flex-1">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-graduation-cap text-blue-600 text-lg"></i>
                        <span className="font-semibold text-blue-900">Profesor Capacitado</span>
                      </div>
                      <p className="text-sm text-blue-700 mt-1">
                        <strong>Marcado:</strong> Acceso completo al sistema • 
                        <strong> No marcado:</strong> Solo acceso a clases de capacitación
                      </p>
                    </Label>
                  </div>
                )}
                
                {/* Checkbox Promotor IBA - Solo para Profesores y Estudiantes */}
                {(editForm.rol === 'profesor' || editForm.rol === 'estudiante') && (
                  <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-2 border-purple-200 hover:border-purple-300 transition-all cursor-pointer">
                    <input
                      type="checkbox"
                      id="promotor_iba"
                      checked={editForm.permisos_adicionales?.promotor_iba || false}
                      onChange={(e) => setEditForm({
                        ...editForm, 
                        permisos_adicionales: {
                          ...editForm.permisos_adicionales,
                          promotor_iba: e.target.checked
                        }
                      })}
                      className="mt-1 h-5 w-5 text-purple-600 focus:ring-2 focus:ring-purple-500 rounded cursor-pointer"
                    />
                    <Label htmlFor="promotor_iba" className="cursor-pointer flex-1">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-star text-purple-600 text-lg"></i>
                        <span className="font-semibold text-purple-900">Promotor IBA</span>
                      </div>
                      <p className="text-sm text-purple-700 mt-1">
                        <strong>Acceso adicional a:</strong> Área de Consultas • Estudiantes Pendientes
                      </p>
                      <p className="text-xs text-purple-600 mt-1 italic">
                        * Mantiene todos los permisos de su rol actual ({editForm.rol})
                      </p>
                    </Label>
                  </div>
                )}

                {/* Checkbox para acceso a Marketing */}
                {(editForm.rol === 'profesor' || editForm.rol === 'estudiante') && (
                  <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-green-50 to-teal-50 rounded-lg border-2 border-green-200 hover:border-green-300 transition-all cursor-pointer">
                    <input
                      type="checkbox"
                      id="acceso_marketing"
                      checked={editForm.permisos_adicionales?.acceso_marketing || false}
                      onChange={(e) => setEditForm({
                        ...editForm, 
                        permisos_adicionales: {
                          ...editForm.permisos_adicionales,
                          acceso_marketing: e.target.checked
                        }
                      })}
                      className="mt-1 h-5 w-5 text-green-600 focus:ring-2 focus:ring-green-500 rounded cursor-pointer"
                    />
                    <Label htmlFor="acceso_marketing" className="cursor-pointer flex-1">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-bullhorn text-green-600 text-lg"></i>
                        <span className="font-semibold text-green-900">Acceso Marketing</span>
                      </div>
                      <p className="text-sm text-green-700 mt-1">
                        <strong>Acceso al CRM:</strong> Pipeline de Prospectos • Comunicaciones • Seguimiento
                      </p>
                      <p className="text-xs text-green-600 mt-1 italic">
                        * Módulo de gestión de prospectos y marketing del Instituto
                      </p>
                    </Label>
                  </div>
                )}

                {/* Checkbox para acceso a Genesis AI */}
                {(editForm.rol === 'profesor' || editForm.rol === 'estudiante') && (
                  <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-2 border-blue-200 hover:border-blue-300 transition-all cursor-pointer">
                    <input
                      type="checkbox"
                      id="genesis_access"
                      checked={editForm.permisos_adicionales?.genesis_access || false}
                      onChange={(e) => setEditForm({
                        ...editForm, 
                        permisos_adicionales: {
                          ...editForm.permisos_adicionales,
                          genesis_access: e.target.checked
                        }
                      })}
                      className="mt-1 h-5 w-5 text-blue-600 focus:ring-2 focus:ring-blue-500 rounded cursor-pointer"
                    />
                    <Label htmlFor="genesis_access" className="cursor-pointer flex-1">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-robot text-blue-600 text-lg"></i>
                        <span className="font-semibold text-blue-900">Genesis AI</span>
                      </div>
                      <p className="text-sm text-blue-700 mt-1">
                        <strong>Acceso al Asistente IA:</strong> Chat con Genesis • Consultas basadas en documentos
                      </p>
                      <p className="text-xs text-blue-600 mt-1 italic">
                        * Asistente de inteligencia artificial del Instituto
                      </p>
                    </Label>
                  </div>
                )}
              </div>
            </div>

            {/* Configuración Especial - Nueva sección */}
            <div className="mb-4">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-orange-100">
                <div className="w-1 h-6 bg-gradient-to-b from-orange-500 to-orange-600 rounded-full"></div>
                Configuración Especial
              </h3>
              <div className="space-y-3">
                {/* Popup Registro - Solo para estudiantes, graduados, pastores */}
                {['estudiante', 'graduado', 'pastor'].includes(editForm.rol) && (
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <input
                      type="checkbox"
                      id="mostrar_popup_registro"
                      checked={editForm.mostrar_popup_registro}
                      onChange={(e) => setEditForm({...editForm, mostrar_popup_registro: e.target.checked})}
                      className="h-5 w-5 text-blue-600 focus:ring-2 focus:ring-blue-500 rounded cursor-pointer"
                    />
                    <Label htmlFor="mostrar_popup_registro" className="cursor-pointer flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">📝</span>
                        <span className="font-medium text-blue-900">Mostrar Popup de Registro</span>
                      </div>
                      <p className="text-xs text-blue-700">Mostrar aviso para completar inscripción al semestre</p>
                    </Label>
                  </div>
                )}

                {/* Botón Enviar Formulario de Registro - Solo para estudiantes */}
                {['estudiante', 'pastor', 'graduado'].includes(editForm.rol) && (
                  <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">📋</span>
                        <div>
                          <span className="font-medium text-indigo-900">Formulario de Registro</span>
                          <p className="text-xs text-indigo-700">Enviar formulario de datos por email</p>
                          {selectedUser?.formulario_registro_completado ? (
                            <p className="text-xs text-green-600 mt-1 font-medium">
                              ✅ Completado el {new Date(selectedUser.formulario_registro_completado_fecha).toLocaleDateString('es-UY')}
                            </p>
                          ) : selectedUser?.formulario_registro_enviado ? (
                            <p className="text-xs text-amber-600 mt-1">
                              📧 Enviado el {new Date(selectedUser.formulario_registro_enviado_fecha).toLocaleDateString('es-UY')} - Pendiente
                            </p>
                          ) : null}
                        </div>
                      </div>
                      {!selectedUser?.formulario_registro_completado && (
                        <Button
                          type="button"
                          size="sm"
                          className="bg-indigo-600 hover:bg-indigo-700 text-white"
                          onClick={async () => {
                            try {
                              const response = await fetch(`${API}/users/${selectedUser.id}/enviar-formulario-registro`, {
                                method: 'POST',
                                headers: {
                                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                                }
                              });
                              const data = await response.json();
                              if (response.ok) {
                                toast.success(data.message || 'Formulario enviado');
                                // Actualizar usuario localmente
                                setUsers(prev => prev.map(u => 
                                  u.id === selectedUser.id 
                                    ? {...u, formulario_registro_enviado: true, formulario_registro_enviado_fecha: new Date().toISOString(), pendiente_formulario_registro: true}
                                    : u
                                ));
                                setSelectedUser(prev => ({...prev, formulario_registro_enviado: true, formulario_registro_enviado_fecha: new Date().toISOString()}));
                              } else {
                                toast.error(data.detail || 'Error al enviar');
                              }
                            } catch (error) {
                              toast.error('Error de conexión');
                            }
                          }}
                        >
                          <Mail className="h-3 w-3 mr-1" />
                          {selectedUser?.formulario_registro_enviado ? 'Reenviar' : 'Enviar'}
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Forzar Notificaciones - Para todos */}
                <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <input
                    type="checkbox"
                    id="force_onesignal_prompt"
                    checked={editForm.force_onesignal_prompt}
                    onChange={(e) => setEditForm({...editForm, force_onesignal_prompt: e.target.checked})}
                    className="h-5 w-5 text-orange-600 focus:ring-2 focus:ring-orange-500 rounded cursor-pointer"
                  />
                  <Label htmlFor="force_onesignal_prompt" className="cursor-pointer flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🔔</span>
                      <span className="font-medium text-orange-900">Forzar Aviso de Notificaciones</span>
                    </div>
                    <p className="text-xs text-orange-700">Mostrar modal para activar notificaciones push</p>
                  </Label>
                </div>

                {/* Graduado IBA - Para todos */}
                <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <input
                    type="checkbox"
                    id="es_graduado"
                    checked={editForm.es_graduado}
                    onChange={(e) => setEditForm({...editForm, es_graduado: e.target.checked})}
                    className="h-5 w-5 text-purple-600 focus:ring-2 focus:ring-purple-500 rounded cursor-pointer"
                  />
                  <Label htmlFor="es_graduado" className="cursor-pointer flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">⭐</span>
                      <span className="font-medium text-purple-900">Graduado IBA (Acceso Área VIP)</span>
                    </div>
                    <p className="text-xs text-purple-700">Acceso al área exclusiva de graduados</p>
                  </Label>
                </div>

                {/* Compromiso de Pago y Cuotas - Solo estudiantes */}
                {editForm.rol === 'estudiante' && (
                  <>
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="compromiso_pago_habilitado"
                        checked={editForm.compromiso_pago_habilitado}
                        onChange={(e) => setEditForm({...editForm, compromiso_pago_habilitado: e.target.checked})}
                        className="h-5 w-5 text-amber-600 focus:ring-2 focus:ring-amber-500 rounded cursor-pointer"
                      />
                      <Label htmlFor="compromiso_pago_habilitado" className="cursor-pointer flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">💳</span>
                          <span className="font-medium text-amber-900">Habilitar Compromiso de Pago</span>
                        </div>
                        <p className="text-xs text-amber-700">Permite inscribirse con pago diferido</p>
                      </Label>
                    </div>
                    {editForm.compromiso_pago_habilitado && (
                      <div className="mt-3 ml-8 space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-amber-700">Plazo:</span>
                          <button
                            type="button"
                            onClick={() => setEditForm({...editForm, compromiso_pago_plazo_max: 15})}
                            className={`px-3 py-1 text-xs rounded-lg border transition-all ${
                              editForm.compromiso_pago_plazo_max === 15
                                ? 'border-amber-500 bg-amber-100 text-amber-800'
                                : 'border-gray-300 hover:border-amber-400'
                            }`}
                          >
                            15 días
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditForm({...editForm, compromiso_pago_plazo_max: 30})}
                            className={`px-3 py-1 text-xs rounded-lg border transition-all ${
                              editForm.compromiso_pago_plazo_max === 30
                                ? 'border-amber-500 bg-amber-100 text-amber-800'
                                : 'border-gray-300 hover:border-amber-400'
                            }`}
                          >
                            30 días
                          </button>
                        </div>
                        <Input
                          placeholder="Motivo (opcional)"
                          value={editForm.compromiso_pago_motivo || ''}
                          onChange={(e) => setEditForm({...editForm, compromiso_pago_motivo: e.target.value})}
                          className="h-8 text-xs"
                        />
                      </div>
                    )}
                  </div>

                  {/* Pago en 3 Cuotas */}
                  <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="pago_cuotas_3_habilitado"
                        checked={editForm.pago_cuotas_3_habilitado}
                        onChange={(e) => setEditForm({...editForm, pago_cuotas_3_habilitado: e.target.checked})}
                        className="h-5 w-5 text-indigo-600 focus:ring-2 focus:ring-indigo-500 rounded cursor-pointer"
                        data-testid="pago-cuotas-3-checkbox"
                      />
                      <Label htmlFor="pago_cuotas_3_habilitado" className="cursor-pointer flex-1">
                        <div className="flex items-center gap-2">
                          <i className="fas fa-file-contract text-indigo-600" />
                          <span className="font-medium text-indigo-900">Habilitar Pago en 3 Cuotas</span>
                        </div>
                        <p className="text-xs text-indigo-700">Permite inscribirse con pago fraccionado en 3 cuotas mensuales con firma de contrato</p>
                      </Label>
                    </div>
                  </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="border-t pt-4 flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setIsEditDialogOpen(false)}
              className="flex items-center gap-2 hover:bg-gray-100 transition-all"
            >
              <XCircle className="h-4 w-4" />
              Cancelar
            </Button>
            <Button 
              onClick={handleEditSubmit}
              className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-md hover:shadow-lg transition-all"
            >
              <CheckCircle className="h-4 w-4" />
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={isResetPasswordDialogOpen} onOpenChange={setIsResetPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-blue-600" />
              Cambiar Contraseña
            </DialogTitle>
            <DialogDescription>
              Asigna una nueva contraseña para <strong>{selectedUser?.nombre} {selectedUser?.apellido}</strong> ({selectedUser?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">Nueva Contraseña</Label>
              <Input
                id="new-password"
                type="text"
                placeholder="Mínimo 6 caracteres"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full"
              />
              <p className="text-xs text-gray-500">
                El usuario podrá usar esta contraseña para iniciar sesión inmediatamente.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setIsResetPasswordDialogOpen(false);
                setNewPassword('');
              }}
              disabled={resettingPassword}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleResetPasswordConfirm}
              disabled={resettingPassword || !newPassword || newPassword.length < 6}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {resettingPassword ? 'Cambiando...' : 'Cambiar Contraseña'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compromiso de Pago Dialog */}
      <Dialog open={isCompromisoDialogOpen} onOpenChange={setIsCompromisoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-amber-500 text-xl">🤝</span>
              Compromiso de Pago
            </DialogTitle>
            <DialogDescription>
              Configura la opción de compromiso de pago para <strong>{selectedUser?.nombre} {selectedUser?.apellido}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Toggle habilitado */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-800">Habilitar Compromiso de Pago</p>
                <p className="text-xs text-gray-500">Permite al estudiante inscribirse con pago diferido</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={compromisoConfig.habilitado}
                  onChange={(e) => setCompromisoConfig({...compromisoConfig, habilitado: e.target.checked})}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
              </label>
            </div>

            {/* Plazo máximo */}
            <div className="space-y-2">
              <Label>Plazo máximo para pagar</Label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setCompromisoConfig({...compromisoConfig, plazo_max: 15})}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                    compromisoConfig.plazo_max === 15
                      ? 'border-amber-500 bg-amber-50 text-amber-700'
                      : 'border-gray-200 hover:border-amber-300'
                  }`}
                >
                  <span className="font-bold text-lg">15</span>
                  <span className="text-sm ml-1">días</span>
                </button>
                <button
                  type="button"
                  onClick={() => setCompromisoConfig({...compromisoConfig, plazo_max: 30})}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                    compromisoConfig.plazo_max === 30
                      ? 'border-amber-500 bg-amber-50 text-amber-700'
                      : 'border-gray-200 hover:border-amber-300'
                  }`}
                >
                  <span className="font-bold text-lg">30</span>
                  <span className="text-sm ml-1">días</span>
                </button>
              </div>
            </div>

            {/* Motivo/Nota */}
            <div className="space-y-2">
              <Label>Motivo (opcional)</Label>
              <Input
                placeholder="Ej: Acuerdo verbal del 10/02, dificultad económica temporal..."
                value={compromisoConfig.motivo}
                onChange={(e) => setCompromisoConfig({...compromisoConfig, motivo: e.target.value})}
              />
              <p className="text-xs text-gray-500">Nota interna para recordar por qué se habilitó</p>
            </div>

            {/* Advertencia */}
            {compromisoConfig.habilitado && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <p className="font-medium mb-1">⚠️ Importante:</p>
                <ul className="text-xs space-y-1 list-disc pl-4">
                  <li>El estudiante podrá inscribirse al semestre sin pagar inmediatamente</li>
                  <li>Tendrá {compromisoConfig.plazo_max} días para subir el comprobante de pago</li>
                  <li>Si no paga a tiempo, su cuenta será bloqueada automáticamente</li>
                  <li>Esta opción es de <strong>uso único</strong> por estudiante</li>
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsCompromisoDialogOpen(false)}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleCompromisoSubmit}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {compromisoConfig.habilitado ? 'Habilitar Compromiso' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Eliminación</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas eliminar al usuario <strong>{selectedUser?.nombre} {selectedUser?.apellido}</strong>?
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Eliminar Usuario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md w-full mx-2 sm:mx-4 max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-green-600" />
              Crear Nuevo Usuario
            </DialogTitle>
            <DialogDescription>
              {createdCredentials ? 
                'Usuario creado exitosamente. Guarda estas credenciales:' :
                'Complete la información del nuevo usuario. Puede asignar una contraseña o generar una automáticamente.'
              }
            </DialogDescription>
          </DialogHeader>
          
          {/* Scrollable Content Container */}
          <div className="flex-1 overflow-y-auto px-1 max-h-[65vh] sm:max-h-[60vh] scroll-smooth scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            {createdCredentials ? (
              // Show credentials after successful creation
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                    <div className="space-y-2">
                      <p className="font-medium text-green-800">Usuario creado exitosamente</p>
                      <div className="space-y-1 text-sm">
                        <p><span className="font-medium">Email:</span> {createdCredentials.email}</p>
                        <p><span className="font-medium">Contraseña:</span> {createdCredentials.password}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
                    <p className="text-xs text-yellow-800">
                      El usuario deberá cambiar su contraseña en el primer inicio de sesión.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              // Show create form
              <div className="space-y-4 pb-4">
                {/* Mobile scroll hint */}
                <div className="block sm:hidden text-center text-xs text-gray-500 py-2 border-b border-gray-200">
                  📱 Desliza hacia abajo para ver todos los campos
                </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="create-nombre">Nombre *</Label>
                  <Input
                    id="create-nombre"
                    value={createForm.nombre}
                    onChange={(e) => setCreateForm({ ...createForm, nombre: e.target.value })}
                    placeholder="Nombre"
                    disabled={creating}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-apellido">Apellido *</Label>
                  <Input
                    id="create-apellido"
                    value={createForm.apellido}
                    onChange={(e) => setCreateForm({ ...createForm, apellido: e.target.value })}
                    placeholder="Apellido"
                    disabled={creating}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="create-email">Email *</Label>
                <Input
                  id="create-email"
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  placeholder="usuario@ejemplo.com"
                  disabled={creating}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="create-celular">Celular</Label>
                <Input
                  id="create-celular"
                  value={createForm.celular}
                  onChange={(e) => setCreateForm({ ...createForm, celular: e.target.value })}
                  placeholder="+598 99 123 456"
                  disabled={creating}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="create-password">
                  Contraseña {!createForm.generar_usuario_provisorio ? '*' : ''}
                </Label>
                <Input
                  id="create-password"
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  placeholder={createForm.generar_usuario_provisorio ? "Se generará automáticamente" : "Ingrese contraseña personalizada"}
                  disabled={creating || createForm.generar_usuario_provisorio}
                  className={createForm.generar_usuario_provisorio ? "bg-gray-100" : ""}
                />
                <div className="text-xs text-gray-500">
                  {createForm.generar_usuario_provisorio ? (
                    <span>✨ Se creará una contraseña temporal que el usuario deberá cambiar</span>
                  ) : (
                    <span>🔐 Mínimo 8 caracteres. El usuario podrá cambiarla en su primer acceso</span>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="create-rol">Rol *</Label>
                <Select 
                  value={createForm.rol} 
                  onValueChange={(value) => setCreateForm({ ...createForm, rol: value })}
                  disabled={creating}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar rol" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="estudiante">Estudiante</SelectItem>
                    <SelectItem value="pastor">Pastor</SelectItem>
                    <SelectItem value="graduado">Graduado</SelectItem>
                    <SelectItem value="profesor">Profesor</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  id="create-generar-provisional"
                  type="checkbox"
                  checked={createForm.generar_usuario_provisorio}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setCreateForm({ 
                      ...createForm, 
                      generar_usuario_provisorio: checked,
                      password: checked ? '' : createForm.password // Limpiar contraseña si se activa generación automática
                    });
                  }}
                  className="rounded text-blue-600"
                  disabled={creating}
                />
                <Label htmlFor="create-generar-provisional" className="text-sm">
                  Generar contraseña automáticamente (en lugar de la contraseña personalizada)
                </Label>
              </div>
            </div>
            )}
          </div>
          
          <DialogFooter className="flex-shrink-0 flex-col sm:flex-row gap-2 pt-4">
            <Button 
              variant="outline" 
              onClick={handleCreateDialogClose}
              disabled={creating}
              className="w-full sm:w-auto"
            >
              {createdCredentials ? 'Cerrar' : 'Cancelar'}
            </Button>
            {!createdCredentials && (
              <Button 
                onClick={handleCreateSubmit}
                disabled={creating || !createForm.nombre || !createForm.apellido || !createForm.email || !createForm.rol || (!createForm.generar_usuario_provisorio && !createForm.password)}
                className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
              >
                {creating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creando...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Crear Usuario
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default GestionUsuarios;
